import axios from "axios";
import { Socio } from '../types/Socio.tsx'

const getUserProfile = async (
  email: string,
  getAccessTokenSilently: () => Promise<string>
): Promise<Socio[]> => {
  try {
    const token = await getAccessTokenSilently();
    const response = await axios.get<Socio[]>(
      `${process.env.REACT_APP_BACK_URL}/socio/email/${email}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error al obtener el perfil de usuario:", error);
    throw error;
  }
};

const createSocio = async (
  socio: Partial<Socio>,
  getAccessTokenSilently: () => Promise<string>
): Promise<Socio> => {
  try {
    const token = await getAccessTokenSilently();
    const response = await axios.post<Socio>(
      `${process.env.REACT_APP_BACK_URL}/socio`,
      socio,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error al crear el socio:', error);
    throw error;
  }
};

const updateSocio = async (
  id: string,
  socio: Partial<Socio>,
  getAccessTokenSilently: () => Promise<string>
): Promise<Socio> => {
  try {
    const token = await getAccessTokenSilently();
    const response = await axios.put<Socio>(
      `${process.env.REACT_APP_BACK_URL}/socio/${id}`,
      socio,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error al actualizar el socio:', error);
    throw error;
  }
};

const uploadSocioLogo = async (
  socioId: string,
  file: File,
  getAccessTokenSilently: () => Promise<string>
): Promise<string> => {
  try {
    const token = await getAccessTokenSilently();
    const formData = new FormData();
    formData.append("file", file);

    const response = await axios.post<{ logoUrl: string }>(
      `${process.env.REACT_APP_BACK_URL}/files/socios/${socioId}/logo`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data.logoUrl;
  } catch (error) {
    console.error("Error al subir el logo del socio:", error);
    throw error;
  }
};

const getSocioById = async (
  socio_id: string,
  getAccessTokenSilently: () => Promise<string>
): Promise<Socio> => {
  try {
    const token = await getAccessTokenSilently();
    const response = await axios.get<Socio>(
      `${process.env.REACT_APP_BACK_URL}/socio/${socio_id}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error al obtener el perfil de usuario:", error);
    throw error;
  }
};

const saveSociosInLocalStorage = (socios: Socio[]) => {
  localStorage.setItem("socios", JSON.stringify(socios));
};

const getSociosFromLocalStorage = (): Socio[] | null => {
  const socios = localStorage.getItem("socios");
  return socios ? JSON.parse(socios) : null;
};

const saveSelectedSocioInLocalStorage = (socio: Socio) => {
  const clean: Record<string, any> = { ...(socio as any) };
  delete clean.pagos_locales;
  delete clean.sos_sync_status;
  localStorage.setItem("socioSeleccionado", JSON.stringify(clean));
};

const getSelectedSocioFromLocalStorage = (): Socio | null => {
  const socio = localStorage.getItem("socioSeleccionado");
  return socio ? JSON.parse(socio) : null;
};
const clearSelectedSocioFromLocalStorage = () => {
  localStorage.removeItem("socioSeleccionado");
};

const clearLocalStorage = () => {
  localStorage.removeItem("socios");
  localStorage.removeItem("socioSeleccionado");
};

const autoSelectSingleSocio = () => {
  const socios = getSociosFromLocalStorage();
  if (socios && socios.length === 1) {
    saveSelectedSocioInLocalStorage(socios[0]);
    return socios[0];
  }
  return null;
};

const checkAndRedirectIfSocioSelected = (): boolean => {
  return !!getSelectedSocioFromLocalStorage();
};

const searchSocios = async (
  searchTerm: string,
  getAccessTokenSilently: () => Promise<string>
): Promise<Socio[]> => {
  try {
    const token = await getAccessTokenSilently();
    const term = String(searchTerm || "").trim();

    const [bySearch, byId] = await Promise.all([
      axios
        .get<Socio[]>(`${process.env.REACT_APP_BACK_URL}/socio/search`, {
          params: { search: term },
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((r) => (Array.isArray(r.data) ? r.data : []))
        .catch(() => []),
      /^\d+$/.test(term)
        ? axios
            .get<Socio>(`${process.env.REACT_APP_BACK_URL}/socio/${term}`, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .then((r) => (r.data ? [r.data] : []))
            .catch(() => [])
        : Promise.resolve([] as Socio[]),
    ]);

    const merged = [...bySearch, ...byId];
    const unique = merged.filter(
      (s, i, arr) => arr.findIndex((x) => String(x?.socio_id) === String(s?.socio_id)) === i
    );

    return unique;
  } catch (error) {
    console.error("Error al buscar socios:", error);
    throw error;
  }
};

const socioService = {
  getSocioById,
  getUserProfile,
  createSocio,
  updateSocio,
  uploadSocioLogo,
  searchSocios,
  saveSociosInLocalStorage,
  getSociosFromLocalStorage,
  saveSelectedSocioInLocalStorage,
  getSelectedSocioFromLocalStorage,
  clearSelectedSocioFromLocalStorage,
  clearLocalStorage,
  autoSelectSingleSocio,
  checkAndRedirectIfSocioSelected,
};

export default socioService;
